import * as THREE from "three";
import ExtrudedLoopSubGeometryBase from "./ExtrudedLoopSubGeometryBase";
import ExtrudedLoopAnnotationData from "../sceneObjectData/ExtrudedLoopAnnotationData";
import { SceneObjectStyle } from "../sceneObject/SceneObjectStyle";

import { LineSegments2 } from "three/addons/lines/LineSegments2.js";
import { LineGeometry } from "three/addons/lines/LineGeometry.js";
import { LineMaterial } from "three/addons/lines/LineMaterial.js";

import { createHitLine } from "../../utils/annotationUtils";
import { RenderOrderEnum } from "../../enums/RenderOrderEnum";
import { sceneUnitsFromMm } from "../../utils/scaler";
import Constants from "../../constants";
import { calculatePolygonAreaAndCentroid } from "../../utils/mathUtils";

// 2D representation of an annotation extruded from a closed loop (Polygon/Rectangle/Circle): a
// true top-down (world XY, unrotated) projection of the full extruded solid — its boundary
// outline, always, plus a fill once depth exceeds the default extrusion depth (below/at the
// default, the shape has no meaningful volume, so no fill is drawn — the plain outline signals
// that to the user). Deliberately does NOT reuse data.getProfilePoints2d()/the shape's real
// placement rotation the way ExtrudedLoopSubGeometry3d does: a flat profile placed on a
// non-horizontal surface (e.g. a wall) would then render edge-on as a degenerate line when viewed
// from directly above in plan view. Instead uses
// data.getTopDownProjectionPoints2d() (a convex hull of the extruded solid's projected vertices,
// so it correctly captures the wall-normal extrusion's own contribution to the footprint too) with
// solidGroup left unrotated. Only the fill's visibility depends on depth, mirroring the same
// Constants.DEFAULT_EXTRUSION_DEPTH_MM threshold ExtrudedLoopSubGeometry3d uses for its
// wireframe/solid switch — the footprint's shape itself never changes with depth once it exceeds
// zero. depthTest is disabled on both materials and renderOrder is bumped to Annotation2d,
// matching how every other flat 2D annotation (e.g. LengthDimensionSubGeometry2d) avoids
// z-fighting against coplanar plan-view geometry.
export default abstract class ExtrudedLoopSubGeometry2d<T extends ExtrudedLoopAnnotationData> extends ExtrudedLoopSubGeometryBase<T> {

    // Raycast distance-ordering is completely independent of depthTest/renderOrder (those only
    // affect the render/color pass) — if this outline/fill sat exactly at z=0, it would be
    // perfectly coplanar with the room's own floor covering (also flat 2D content at z=0), and
    // the raycaster could just as easily return the floor as the nearer hit, silently swallowing
    // clicks on the shape's body while the area label (already lifted above z=0 by labelZOffset)
    // kept working. Lifting the outline/fill by this smaller amount guarantees it raycasts closer
    // to the top-down camera than the floor, while staying below the label so THAT ordering
    // (label nearer than outline/fill) stays correct too.
    private static readonly zOffset = sceneUnitsFromMm(2);

    // LineSegments2 (the fat-line implementation backing both `edges` and its wider `edgesHitLine`
    // hit target) raycasts unreliably under an OrthographicCamera — which the 2D plan view always
    // uses — so relying on the line alone left the outline unclickable in 2D (only the area label,
    // a plain Mesh, worked). Fixed by keeping `fillMesh` always present and visible as a real,
    // camera-agnostic raycast target for the whole closed shape once one exists, swapping to this
    // fully transparent material (visible = true, alpha 0 — never visible = false, or the app's
    // isFullyVisible() raycast filter would discard it) when the shape isn't "really" filled.
    private static readonly invisibleHitMaterial = new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        depthWrite: false,
        depthTest: false,
        // Mesh raycasting respects material.side (FrontSide by default, culling hits from the
        // "back"); the projected hull's winding isn't guaranteed to face the top-down camera, so
        // this must be DoubleSide too, same as the real fill material — omitting it here was the
        // actual bug: it silently made the default-depth (invisible-material) case unraycastable
        // whenever the hull happened to wind the "wrong" way, while the filled case kept working.
        side: THREE.DoubleSide
    });

    private fillMesh: THREE.Mesh;
    private fillMaterial: THREE.MeshBasicMaterial;
    private showFill: boolean = false;

    private edgesGeometry: LineGeometry;
    private readonly edges: LineSegments2;
    private readonly edgesHitLine: LineSegments2; // Invisible, wider hit target kept in sync with edgesGeometry
    private edgesMaterial: LineMaterial;

    // Leaf classes construct their own initial (Normal-style) materials from their own static
    // caches, so each shape type keeps independent GPU resources, then pass them here.
    protected constructor(initialLineMaterial: LineMaterial, initialFillMaterial: THREE.MeshBasicMaterial, initialTextMaterial: THREE.MeshBasicMaterial) {
        super(initialTextMaterial);

        this.fillMaterial = initialFillMaterial;
        this.fillMesh = new THREE.Mesh(new THREE.BufferGeometry(), this.fillMaterial);

        this.edgesMaterial = initialLineMaterial;
        this.edgesGeometry = new LineGeometry();
        this.edges = new LineSegments2(this.edgesGeometry, this.edgesMaterial);
        this.edgesHitLine = createHitLine(this.edgesGeometry);

        this.solidGroup.add(this.fillMesh);
        this.solidGroup.add(this.edges);
        this.solidGroup.add(this.edgesHitLine);
        this.solidGroup.add(this.textGroup);

        this.group.traverse((obj) => {
            obj.renderOrder = RenderOrderEnum.Annotation2d;
        });
    }

    // Leaf classes resolve a style to their own cached materials (see e.g. AreaAnnotationGeometry2d).
    protected abstract getLineMaterialForStyle(style: SceneObjectStyle): LineMaterial;
    protected abstract getFillMaterialForStyle(style: SceneObjectStyle): THREE.MeshBasicMaterial;

    protected updateGeometry(data: T, forceUpdate: boolean = false): boolean {

        const profile = this.computeProfile(data);
        if (!profile) {
            this.edges.visible = false;
            this.edgesHitLine.visible = false;
            this.textGroup.visible = false;
            this.fillMesh.visible = false;
            return false;
        }
        const { points2d, depth, isOpenPreview } = profile;

        if (isOpenPreview) {
            // Still being drawn (fewer than 3 non-collinear points) — not enough to build a real
            // top-down projection yet, so fall back to the local-basis preview line under the
            // shape's real (possibly non-horizontal) placement transform, same as before this fix.
            this.solidGroup.position.copy(data.placement.location);
            this.solidGroup.quaternion.copy(data.placement.getRotationQuaternion());

            this.updateOpenPreviewLine(points2d);
            this.edges.visible = true;
            this.edgesHitLine.visible = true;
            this.textGroup.visible = false;
            this.fillMesh.visible = false;
        } else {
            // True top-down (world XY) projection of the full extruded solid, flattened —
            // unlike the 3D view, this must NOT inherit the shape's real 3D orientation, or a
            // shape placed on a non-horizontal surface (e.g. a wall) would render edge-on as a
            // degenerate line when viewed from directly above.
            this.solidGroup.position.set(data.placement.location.x, data.placement.location.y, 0);
            this.solidGroup.quaternion.identity();

            const topDownPoints = data.getTopDownProjectionPoints2d();

            this.updateOutline(topDownPoints);
            this.edges.visible = true;
            this.edgesHitLine.visible = true;

            // The area/volume labels show the shape's real enclosed area/volume, which is only
            // meaningful to overlay on this flattened top-down footprint when the profile actually
            // lies in the XY plane (placement.axis parallel to world Z, e.g. a floor/ceiling
            // annotation) — the footprint IS the profile then. For any other orientation (e.g. a
            // wall), the footprint is a projection of the extruded solid, not the profile, so
            // labeling it with the profile's area/volume would misrepresent what's shown — gate
            // both the same way, independent of each other otherwise (see updateAreaLabel()).
            // Same threshold AddExtrudedLoopAnnotationCmd.deriveRefDirection() uses to classify a
            // surface as "horizontal" at creation time — must stay in sync (see the matching note
            // in ExtrudedLoopSubGeometryBase.updateAreaLabelRotation()), or a floor/ceiling
            // annotation whose axis isn't *exactly* (0,0,1) (floating-point noise from the raycast
            // normal) would wrongly lose its area/volume label here.
            const isProfileOnXYPlane = Math.abs(data.placement.axis.z) > Constants.HORIZONTAL_SURFACE_DOT_THRESHOLD;
            const { centroid } = calculatePolygonAreaAndCentroid(topDownPoints);
            // World +Z (not data.placement.axis) — the flattened view always reads like a
            // normal plan-view label regardless of the annotation's real surface orientation.
            this.updateAreaLabel(
                data.computeEnclosedArea(), data.computeEnclosedVolume(),
                data.showAreaText && isProfileOnXYPlane, data.showVolume && isProfileOnXYPlane,
                centroid, ExtrudedLoopSubGeometryBase.labelZOffset, new THREE.Vector3(0, 0, 1), data.fontSize
            );

            // At or below the default depth the shape has no meaningful volume — leave it looking
            // like a plain outline (no visible fill color) so the user can tell at a glance,
            // rather than filling every flat (essentially 2D) annotation the same as a genuinely
            // thick one. The mesh itself, though, always stays visible=true and populated — it's
            // still the click target for the shape's whole area either way (see
            // invisibleHitMaterial above), just invisible when not "really" filled.
            this.showFill = depth > sceneUnitsFromMm(Constants.DEFAULT_EXTRUSION_DEPTH_MM);
            this.updateFill(topDownPoints);
            this.fillMesh.visible = true;
            this.fillMesh.material = this.showFill ? this.fillMaterial : ExtrudedLoopSubGeometry2d.invisibleHitMaterial;
        }

        this.data = data.clone();
        return true;
    }

    private updateOpenPreviewLine(points2d: THREE.Vector2[]): void {

        const segments: number[] = [];
        for (let i = 0; i < points2d.length - 1; i++) {
            const p = points2d[i];
            const next = points2d[i + 1];
            segments.push(p.x, p.y, 0, next.x, next.y, 0);
        }

        this.edgesGeometry.dispose();
        this.edgesGeometry = new LineGeometry();
        this.edgesGeometry.setPositions(segments);
        this.edges.geometry = this.edgesGeometry;
        this.edgesHitLine.geometry = this.edgesGeometry;
        this.edges.computeLineDistances();
    }

    // Single closed loop — the flat top-down outline, lifted zOffset above the true z=0 plane
    // (see zOffset's comment above). Unlike the 3D wireframe box, there are no top-face or
    // vertical edges to draw (there is no "top" or "side" once flattened).
    private updateOutline(points2d: THREE.Vector2[]): void {

        const z = ExtrudedLoopSubGeometry2d.zOffset;
        const segments: number[] = [];
        for (let i = 0; i < points2d.length; i++) {
            const p = points2d[i];
            const next = points2d[(i + 1) % points2d.length];
            segments.push(p.x, p.y, z, next.x, next.y, z);
        }

        this.edgesGeometry.dispose();
        this.edgesGeometry = new LineGeometry();
        this.edgesGeometry.setPositions(segments);
        this.edges.geometry = this.edgesGeometry;
        this.edgesHitLine.geometry = this.edgesGeometry;
        this.edges.computeLineDistances();
    }

    private updateFill(points2d: THREE.Vector2[]): void {
        const shape = new THREE.Shape(points2d);
        const geometry = new THREE.ShapeGeometry(shape);
        geometry.translate(0, 0, ExtrudedLoopSubGeometry2d.zOffset); // ShapeGeometry always builds at z=0

        this.fillMesh.geometry.dispose();
        this.fillMesh.geometry = geometry;
    }

    setStyle(style: SceneObjectStyle): void {
        if (this.style === style) return;

        const lineMat = this.getLineMaterialForStyle(style);
        this.edges.material = lineMat;
        this.edgesMaterial = lineMat;

        this.fillMaterial = this.getFillMaterialForStyle(style);
        this.fillMesh.material = this.showFill ? this.fillMaterial : ExtrudedLoopSubGeometry2d.invisibleHitMaterial;

        super.setStyle(style);
    }

    dispose(): void {
        this.edgesGeometry.dispose();
        this.fillMesh.geometry.dispose();
        super.dispose();
    }
}
